import React, { useEffect, useRef } from "react";

const CLUSTERS = [
  { x: 84, y: 66, nodes: [[0, 0], [-26, -14], [22, -18], [-18, 20], [24, 16]], stubs: [[48, 34]] },
  { x: 312, y: 58, nodes: [[0, 0], [-24, 14], [20, -16], [26, 18]], stubs: [[-50, 30]] },
  { x: 104, y: 184, nodes: [[0, 0], [-22, -16], [26, -10], [-14, 20], [18, 22], [40, 8]], stubs: [[56, -26]] },
  { x: 312, y: 182, nodes: [[0, 0], [-26, -12], [22, -20], [-8, 22]], stubs: [[-54, -32]] },
];

const linksOf = (nodes) => [...nodes.slice(1).map((node) => [nodes[0], node]), [nodes[1], nodes[2]]];

export const FragmentedNetwork = ({ labels = [] }) => (
  <svg className="tour-network tour-network--fragmented" viewBox="34 14 332 212" role="img" aria-label="Buyer needs scattered across separate groups that never connect">
    {CLUSTERS.map((cluster, index) => (
      <g key={index} className="tour-network__cluster" style={{ "--i": index }}>
        <g transform={`translate(${cluster.x} ${cluster.y})`}>
          {linksOf(cluster.nodes).map(([a, b], link) => (
            <line key={link} className="tour-network__link" x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
          ))}
          {cluster.stubs.map(([dx, dy], stub) => (
            <line key={stub} className="tour-network__stub" x1={0} y1={0} x2={dx} y2={dy} />
          ))}
          {cluster.nodes.map(([nx, ny], node) => (
            <circle key={node} className="tour-network__node" cx={nx} cy={ny} r={node === 0 ? 4.5 : 3} />
          ))}
          {labels[index] && (
            <text className="tour-network__label" x={0} y={cluster.y < 120 ? 46 : -34} textAnchor="middle">
              {labels[index]}
            </text>
          )}
        </g>
      </g>
    ))}
  </svg>
);

const fibonacciShell = (count, radius) => {
  const points = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i += 1) {
    const y = 1 - (i / (count - 1)) * 2;
    const ring = Math.sqrt(1 - y * y);
    const theta = golden * i;
    points.push([Math.cos(theta) * ring * radius, y * radius, Math.sin(theta) * ring * radius]);
  }
  return points;
};

const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

const nearest = (point, pool, count, skip) =>
  pool
    .map((other, index) => ({ index, d: distance(point, other) }))
    .filter((entry) => entry.index !== skip)
    .sort((x, y) => x.d - y.d)
    .slice(0, count)
    .map((entry) => entry.index);

const buildSphere = () => {
  const outer = fibonacciShell(84, 1);
  const inner = fibonacciShell(30, 0.58);
  const points = [...outer, ...inner];
  const edges = new Map();
  const add = (a, b) => edges.set(a < b ? `${a}-${b}` : `${b}-${a}`, [Math.min(a, b), Math.max(a, b)]);
  outer.forEach((p, i) => nearest(p, outer, 3, i).forEach((j) => add(i, j)));
  inner.forEach((p, i) => nearest(p, inner, 3, i).forEach((j) => add(outer.length + i, outer.length + j)));
  inner.forEach((p, i) => nearest(p, outer, 2, -1).forEach((j) => add(outer.length + i, j)));
  const list = [...edges.values()];
  const neighbors = points.map(() => []);
  list.forEach(([a, b], index) => {
    neighbors[a].push(index);
    neighbors[b].push(index);
  });
  return { points, edges: list, neighbors, outerCount: outer.length };
};

const PULSES = 7;
const PULSE_MS = 1100;

export const ConnectedSphere = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext?.("2d");
    if (!context) return undefined;
    const { points, edges, neighbors, outerCount } = buildSphere();
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const pulses = Array.from({ length: PULSES }, (_, i) => ({
      edge: (i * 37) % edges.length,
      forward: true,
      // Seeded on the first frame: requestAnimationFrame time is page-age,
      // not zero, so a fixed negative start would expire every pulse at once.
      start: null,
    }));
    let width = 0;
    let height = 0;
    let frame = 0;
    let angle = 0.6;
    let last = 0;

    const size = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const project = (point) => {
      const cosY = Math.cos(angle);
      const sinY = Math.sin(angle);
      const x1 = point[0] * cosY - point[2] * sinY;
      const z1 = point[0] * sinY + point[2] * cosY;
      const tilt = 0.38;
      const y2 = point[1] * Math.cos(tilt) - z1 * Math.sin(tilt);
      const z2 = point[1] * Math.sin(tilt) + z1 * Math.cos(tilt);
      const radius = Math.min(width, height) * 0.4;
      const scale = 3 / (3 - z2);
      return { x: width / 2 + x1 * radius * scale, y: height / 2 + y2 * radius * scale, depth: (z2 + 1) / 2 };
    };

    const draw = (now) => {
      const delta = last ? Math.min(now - last, 50) : 16;
      last = now;
      if (!still) angle += delta * 0.00018;
      context.clearRect(0, 0, width, height);

      const glow = context.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.min(width, height) * 0.5);
      glow.addColorStop(0, "rgba(124, 196, 255, 0.22)");
      glow.addColorStop(1, "rgba(124, 196, 255, 0)");
      context.fillStyle = glow;
      context.fillRect(0, 0, width, height);

      const projected = points.map(project);
      edges.forEach(([a, b]) => {
        const pa = projected[a];
        const pb = projected[b];
        const depth = (pa.depth + pb.depth) / 2;
        context.strokeStyle = `rgba(159, 211, 255, ${0.06 + depth * 0.42})`;
        context.lineWidth = 0.6 + depth * 0.7;
        context.beginPath();
        context.moveTo(pa.x, pa.y);
        context.lineTo(pb.x, pb.y);
        context.stroke();
      });

      projected
        .map((p, index) => ({ ...p, index }))
        .sort((a, b) => a.depth - b.depth)
        .forEach((p) => {
          context.fillStyle = `rgba(${p.index >= outerCount ? "255, 255, 255" : "159, 211, 255"}, ${0.25 + p.depth * 0.75})`;
          context.beginPath();
          context.arc(p.x, p.y, 1 + p.depth * 2.2, 0, Math.PI * 2);
          context.fill();
        });

      if (!still) {
        pulses.forEach((pulse, index) => {
          if (pulse.start === null) pulse.start = now - index * (PULSE_MS / PULSES);
          let t = (now - pulse.start) / PULSE_MS;
          if (t < 0) return;
          if (t >= 1) {
            const [a, b] = edges[pulse.edge];
            const at = pulse.forward ? b : a;
            const options = neighbors[at].filter((edge) => edge !== pulse.edge);
            pulse.edge = options[Math.floor(Math.random() * options.length)] ?? pulse.edge;
            pulse.forward = edges[pulse.edge][0] === at;
            pulse.start = now;
            t = 0;
          }
          const [a, b] = edges[pulse.edge];
          const from = projected[pulse.forward ? a : b];
          const to = projected[pulse.forward ? b : a];
          const x = from.x + (to.x - from.x) * t;
          const y = from.y + (to.y - from.y) * t;
          const depth = from.depth + (to.depth - from.depth) * t;
          const halo = context.createRadialGradient(x, y, 0, x, y, 9);
          halo.addColorStop(0, `rgba(255, 255, 255, ${0.5 + depth * 0.5})`);
          halo.addColorStop(1, "rgba(124, 196, 255, 0)");
          context.fillStyle = halo;
          context.beginPath();
          context.arc(x, y, 9, 0, Math.PI * 2);
          context.fill();
        });
        frame = requestAnimationFrame(draw);
      }
    };

    size();
    const onResize = () => {
      size();
      if (still) draw(0);
    };
    // Observe the canvas itself: the tour's grid can change its size without
    // a window resize (scrollbar, drawer, breakpoint collapse).
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(onResize);
    if (observer) observer.observe(canvas);
    else window.addEventListener("resize", onResize);
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="tour-network tour-network--sphere"
      role="img"
      aria-label="Every member connected in one network, with buyer demand moving between them"
    />
  );
};
